'use client';

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { MaterialesLayout } from "./MaterialesLayout";
import { getApiBaseUrl } from "@/lib/apiBaseUrl";

type ResourceLink = {
  id: string;
  label: string;
  iconBg: string;
  iconEmoji: string;
};

const DEFAULT_LINKTREE_BUTTON_COLOR = "#5B4BDB";
const DEFAULT_LINKTREE_SECTION_COLOR = "#5B4BDB";
const DEFAULT_LINKTREE_ICON_BG = "#0F172A";

const isHexColor = (value: unknown): value is string =>
  typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value.trim());

function isImageIcon(icon: string) {
  const v = (icon || "").trim().toLowerCase();
  return v.startsWith("/uploads/") || v.startsWith("http://") || v.startsWith("https://");
}

export default function MaterialesEducativosPage() {
  const [materialLinks, setMaterialLinks] = useState<Record<string, string>>({});
  const [linktreeConfigLoaded, setLinktreeConfigLoaded] = useState(false);
  const [linktreeConfig, setLinktreeConfig] = useState<{
    visible: boolean;
    name: string;
    color: string;
    sectionColor: string;
    icon: string;
    iconBg: string;
  }>({
    visible: false,
    name: "Linktree",
    color: DEFAULT_LINKTREE_BUTTON_COLOR,
    sectionColor: DEFAULT_LINKTREE_SECTION_COLOR,
    icon: "🔗",
    iconBg: DEFAULT_LINKTREE_ICON_BG,
  });

  const resourceLinks: ResourceLink[] = [
    ...(linktreeConfig.visible
      ? [
          {
            id: "linktree",
            label: linktreeConfig.name,
            iconBg: "",
            iconEmoji: linktreeConfig.icon || "🔗",
          },
        ]
      : []),
    {
      id: "guias-secuencias",
      label: "Guías y Secuencias didácticas",
      iconBg: "bg-[#1FBED6]",
      iconEmoji: "📘",
    },
    {
      id: "efemerides",
      label: "Actividades por efemérides",
      iconBg: "bg-[#9AD743]",
      iconEmoji: "📅",
    },
    {
      id: "calendarios",
      label: "Calendarios ambientales descargables",
      iconBg: "bg-[#F37E45]",
      iconEmoji: "🗓️",
    },
    {
      id: "videos-podcast",
      label: "Videos y podcast",
      iconBg: "bg-[#B35DD9]",
      iconEmoji: "🎬",
    },
    {
      id: "lina-amigos",
      label: "Lina y los Amigos del Arcoíris",
      iconBg: "bg-[#34C759]",
      iconEmoji: "🌈",
    },
    {
      id: "blog-escuelas",
      label: "Blog Escuelas",
      iconBg: "bg-[#3B7C3C]",
      iconEmoji: "📝",
    },
  ];

  // Cargar labels configurables desde el backend
  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/material-links`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { slug: string; label: string }[] | null) => {
        if (cancelled || !Array.isArray(data)) return;
        const map: Record<string, string> = {};
        for (const item of data) {
          if (item.slug && item.label) {
            map[item.slug] = item.label;
          }
        }
        setMaterialLinks(map);
      })
      .catch(() => {
        // Si falla, seguimos usando los labels por defecto
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/linktree/config`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setLinktreeConfig((prev) => ({
          visible: data.visible !== false,
          name: typeof data.name === "string" && data.name.trim() ? data.name.trim() : "Linktree",
          color: isHexColor(data.color) ? data.color.trim() : prev.color,
          sectionColor: isHexColor(data.sectionColor) ? data.sectionColor.trim() : prev.sectionColor,
          icon: typeof data.icon === "string" && data.icon.trim() ? data.icon.trim() : "🔗",
          iconBg: isHexColor(data.iconBg) ? data.iconBg.trim() : prev.iconBg,
        }));
        setLinktreeConfigLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLinktreeConfigLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <MaterialesLayout>
      <div className="mx-auto max-w-5xl">
        <div className="rounded-2xl bg-[#E9F8D7] p-6 shadow-md md:p-8">
          <div className="mb-8 flex justify-center">
            <Image
              src="/images/Logo-loop-gp-escuelas.gif"
              alt="Greenpeace en las escuelas"
              width={320}
              height={90}
              className="h-auto w-full max-w-[320px] md:max-w-[380px]"
              unoptimized
            />
          </div>
          <section aria-label="Secciones de materiales educativos" className="space-y-4">
            {(linktreeConfigLoaded ? resourceLinks : resourceLinks.filter((item) => item.id !== "linktree")).map((item) => (
              <Link
                key={item.id}
                href={`/materiales-educativos/${item.id}`}
                className="relative flex items-center gap-4 rounded-2xl px-4 py-4 text-left text-white shadow-md transition hover:opacity-95"
                style={{
                  backgroundColor:
                    item.id === "linktree" ? linktreeConfig.color : "#3B7C3C",
                }}
              >
                {item.id === "linktree" ? (
                  <span className="absolute right-3 top-2 rounded-full border border-[#F5D67A] bg-gradient-to-r from-[#FFD54A] via-[#F6C453] to-[#E2A93A] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#5C3B00] shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_4px_12px_rgba(0,0,0,0.2)] animate-pulse">
                    Novedades
                  </span>
                ) : null}
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-full text-xl ${item.iconBg}`}
                  style={item.id === "linktree" ? { backgroundColor: linktreeConfig.iconBg } : undefined}
                >
                  {item.id === "linktree" && isImageIcon(linktreeConfig.icon) ? (
                    <img
                      src={
                        linktreeConfig.icon.startsWith("http")
                          ? linktreeConfig.icon
                          : `${getApiBaseUrl()}${linktreeConfig.icon}`
                      }
                      alt="Ícono Linktree"
                      className="h-9 w-9 rounded-full object-cover"
                    />
                  ) : (
                    item.iconEmoji
                  )}
                </span>
                <span className="text-sm font-semibold md:text-base">
                  {materialLinks[item.id] ?? item.label}
                </span>
              </Link>
            ))}
          </section>
        </div>
      </div>
    </MaterialesLayout>
  );
}

